import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";
import CodeEvidencePanel from "./CodeEvidencePanel";
import { Time } from "./time";

// One item at a time. Everything below is untrusted: it is rendered as React text nodes only
// (escaped). No dangerouslySetInnerHTML, no markdown. Links are restricted to http(s).

const PRIORITY_LABEL = {
  user_report: ["User report", "warning"],
  appeal_upheld: ["Appeal upheld", "info"],
  detection: ["Detection", "danger"],
  unsure_earlier: ["Marked unsure earlier", "neutral"],
  sampled: ["Random sample", "neutral"],
};
const LABEL_COLORS = { malicious: "var(--hs-danger)", suspicious: "var(--hs-warning)", benign: "var(--hs-safe)", unsure: "var(--hs-text-muted)" };
const safeHref = (u) => (/^https?:\/\//i.test(u || "") ? u : null);

// Splits `text` into plain / highlighted segments by non-overlapping [start,end) regions.
export function segments(text, highlights) {
  const out = [];
  let pos = 0;
  (highlights || []).forEach((h, i) => {
    if (h.start > pos) out.push({ text: text.slice(pos, h.start), hl: null });
    out.push({ text: text.slice(h.start, h.end), hl: i, rules: h.rule_ids });
    pos = h.end;
  });
  if (pos < text.length) out.push({ text: text.slice(pos), hl: null });
  return out;
}

function HighlightedText({ text, highlights, current, setRef }) {
  return (
    <pre data-testid="review-text" style={{ margin: 0, maxHeight: 460, overflow: "auto", fontSize: 12, whiteSpace: "pre-wrap",
      wordBreak: "break-word", background: "var(--hs-code-bg)", padding: 8, border: "1px solid var(--hs-border)" }}>
      {segments(text, highlights).map((s, i) => (s.hl === null
        ? <span key={i}>{s.text}</span>
        : <mark key={i} data-testid="hl" data-index={s.hl} title={(s.rules || []).join(", ")} ref={setRef(s.hl)}
                className={current === s.hl ? "hs-mark hs-mark-current" : "hs-mark"}
                style={{ padding: "0 1px", outline: current === s.hl ? "2px solid var(--hs-text)" : undefined }}>{s.text}</mark>))}
    </pre>
  );
}

// Who is in the queue: threat-level signals, user reports, upheld appeals. Model-cleared items (benign,
// confidence >= 0.85) are hidden unless the reviewer asks for them.
function QueueCounts({ counts, cleared, setCleared, styles }) {
  if (!counts) return null;
  const chip = (label, n, testid) => <span data-testid={testid} style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>{label} <strong>{n ?? 0}</strong></span>;
  return (
    <span style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }} data-testid="review-counts">
      {chip("detections", counts.detection, "count-detection")}
      {chip("user reports", counts.user_report, "count-user-report")}
      {chip("appeals", counts.appeal_upheld, "count-appeal")}
      {chip("unsure earlier", counts.unsure_earlier, "count-unsure")}
      <label style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>
        Cleared by model <strong data-testid="count-cleared">{counts.cleared_by_model ?? 0}</strong>{" "}
        <select data-testid="review-cleared-filter" style={styles.select} value={cleared} onChange={(e) => setCleared(e.target.value)}>
          <option value="hide">hidden</option><option value="only">show only these</option><option value="all">include</option>
        </select>
      </label>
    </span>
  );
}

export default function ReviewQueue({ styles }) {
  const [data, setData] = useState(null);
  const [skipped, setSkipped] = useState([]);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [note, setNote] = useState("");
  const [feedbackRule, setFeedbackRule] = useState("");
  const [feedbackNote, setFeedbackNote] = useState("");
  const [armed, setArmed] = useState(false);
  const [cur, setCur] = useState(-1);
  const [cleared, setCleared] = useState("hide");   // "Cleared by model" group: hide | only | all
  const refs = useRef({});
  const armTimer = useRef(null);
  const busy = useRef(false);

  const load = useCallback(async (exclude) => {
    try {
      const r = await api.reviewNext(exclude, cleared);
      setData(r.data); setErr(""); setCur(-1); setFeedbackRule("");
    } catch (ex) { setErr(ex.message); }
  }, [cleared]);
  useEffect(() => { load(skipped); }, [load, skipped]);

  const item = data && !data.empty ? data.item : null;
  const canLabel = !!(item && data.snapshot && !data.snapshot.content_missing && item.current_snapshot_id);

  const decide = useCallback(async (verdict) => {
    if (!item || busy.current) return;
    if (!canLabel) { setMsg("Nothing to label yet: this item has no snapshot content."); return; }
    busy.current = true;
    try {
      const r = await api.humanVerdict(item.id, { verdict, snapshot_id: item.current_snapshot_id, note, techniques: [] });
      setMsg(`Recorded "${verdict}" for ${data.server.name} — tier is now ${r.data.tier}.`);
      setNote(""); setArmed(false);
      if (verdict === "unsure") { setSkipped((s) => [...s, item.id]); } else { await load(skipped); }
    } catch (ex) {
      setMsg(ex.status === 409 ? "The content changed since you opened it — reloading." : "Error: " + ex.message);
      await load(skipped);
    } finally { busy.current = false; }
  }, [item, canLabel, note, data, load, skipped]);

  const next = useCallback(() => { if (item) setSkipped((s) => [...s, item.id]); setMsg(""); }, [item]);

  const pressMalicious = useCallback(() => {
    if (armed) { clearTimeout(armTimer.current); decide("malicious"); return; }
    setArmed(true); setMsg("Press m again (or click Malicious) within 5 seconds to confirm — this makes the item public.");
    clearTimeout(armTimer.current);
    armTimer.current = setTimeout(() => { setArmed(false); setMsg(""); }, 5000);
  }, [armed, decide]);

  const jump = useCallback(() => {
    const n = data?.snapshot?.highlights?.length || 0;
    if (!n) return;
    const nextIdx = (cur + 1) % n;
    setCur(nextIdx);
    const el = refs.current[nextIdx];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [cur, data]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag) || e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === "b") { e.preventDefault(); decide("benign"); }
      else if (k === "u") { e.preventDefault(); decide("unsure"); }
      else if (k === "m") { e.preventDefault(); pressMalicious(); }
      else if (k === "n") { e.preventDefault(); next(); }
      else if (k === "j") { e.preventDefault(); jump(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [decide, pressMalicious, next, jump]);
  useEffect(() => () => clearTimeout(armTimer.current), []);

  const sendFeedback = async () => {
    if (!feedbackRule) return;
    try {
      await api.ruleFeedback({ item_id: item.id, rule_id: feedbackRule, note: feedbackNote });
      setMsg(`Thanks — recorded "${feedbackRule} is too broad" for ${data.server.name}.`);
      setFeedbackNote(""); setFeedbackRule("");
    } catch (ex) { setMsg("Error: " + ex.message); }
  };

  if (err) return <div style={{ ...styles.card, color: "var(--hs-danger)" }}>Could not load the queue: {err}</div>;
  if (!data) return <div style={styles.card}>Loading…</div>;
  const q = data.queue;
  const progress = (
    <div style={{ ...styles.card, display: "flex", gap: 16, alignItems: "center", fontSize: 13 }} data-testid="review-progress">
      <strong>{q.left} left</strong><span>{q.reviewed_today} reviewed today</span>
      {q.skipped > 0 && <span style={{ color: "var(--hs-text-muted)" }}>{q.skipped} skipped this session
        <button style={{ ...styles.btnDim, marginLeft: 8 }} onClick={() => setSkipped([])}>show again</button></span>}
      <QueueCounts counts={q.counts} cleared={cleared} setCleared={setCleared} styles={styles} />
      <span style={{ marginLeft: "auto", color: "var(--hs-text-muted)", fontSize: 12 }}>keys: b benign · u unsure · m malicious (twice) · n next · j next highlight</span>
    </div>
  );
  if (data.empty) {
    return (
      <div data-testid="review-page">
        <h2>Review</h2>{progress}
        <div style={styles.card} data-testid="review-empty">Nothing left to review{q.skipped > 0 ? " (apart from the items you skipped)" : ""}.</div>
      </div>
    );
  }
  const pri = PRIORITY_LABEL[data.priority] || [data.priority, "neutral"];
  const hl = data.snapshot?.highlights || [];
  const setRef = (i) => (el) => { refs.current[i] = el; };
  return (
    <div data-testid="review-page">
      <h2>Review</h2>
      {progress}
      {msg && <div style={{ ...styles.card, color: "var(--hs-accent)" }} data-testid="review-msg">{msg}</div>}

      <div style={styles.card} data-testid="review-header">
        <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
          <span style={{ fontSize: 20, fontWeight: 600 }} data-testid="server-name">{data.server.name}</span>
          <span className={`hs-badge hs-badge-${pri[1]}`}>{pri[0]}</span>
          <span style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>tier {data.item.current_tier} · score {Number(data.total_score || 0).toFixed(3)}</span>
        </div>
        <div style={{ fontSize: 12, marginTop: 4 }}>
          Publisher: <strong data-testid="publisher">{data.server.publisher || "unknown"}</strong>
          {data.server.version && <> · version {data.server.version}</>}
        </div>
        {data.server.description && <div style={{ fontSize: 12, color: "var(--hs-text-muted)", marginTop: 4 }}>{data.server.description}</div>}
        <div style={{ fontSize: 12, marginTop: 6 }} data-testid="review-links">
          {data.links.filter((l) => safeHref(l.url)).map((l) => (
            <a key={l.url} href={safeHref(l.url)} target="_blank" rel="noopener noreferrer nofollow"
               style={{ color: "var(--hs-accent)", marginRight: 12 }}>{l.label}</a>))}
          {data.links.length === 0 && <span style={{ color: "var(--hs-text-muted)" }}>no links known</span>}
        </div>
        {(data.meta?.remotes || []).length > 0 && (
          <div style={{ fontSize: 12, color: "var(--hs-text-muted)", marginTop: 4 }}>Remote endpoints (not linked): {data.meta.remotes.join(", ")}</div>)}
      </div>

      {item.type === "mcp_server" && <CodeEvidencePanel key={item.id} itemId={item.id} styles={styles} />}

      {data.reports.length > 0 && (
        <div style={{ ...styles.card, borderColor: "var(--hs-warning)" }} data-testid="review-reports">
          <h3 style={{ marginTop: 0 }}>User reports ({data.reports.length})</h3>
          {data.reports.map((r) => (
            <div key={r.id} style={{ fontSize: 12, marginBottom: 8, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              <span style={{ color: "var(--hs-text-muted)" }}><Time value={r.created_at} /> · reported as “{r.server_input}”</span>
              <div>{r.what_happened}</div>
            </div>))}
        </div>)}

      <div style={styles.card} data-testid="why-flagged">
        <h3 style={{ marginTop: 0 }}>Why flagged</h3>
        <div style={{ fontSize: 12, color: "var(--hs-text-muted)", marginBottom: 6 }}>
          Total score {Number(data.total_score || 0).toFixed(3)} · suspicious ≥ {data.thresholds.suspicious}, malicious ≥ {data.thresholds.malicious}
          {" "}({data.evidence_source === "stored" ? "from the stored verdict; content not available" : "recomputed from the content"})
        </div>
        {data.why_flagged.length === 0 && <div style={{ color: "var(--hs-text-muted)", fontSize: 12 }}>No rule fired{data.priority === "user_report" ? " — this item is here because of a user report." : "."}</div>}
        {data.why_flagged.map((w) => (
          <div key={w.rule_id} style={{ marginBottom: 10, fontSize: 12 }} data-testid={`why-${w.rule_id}`}>
            <code>{w.rule_id}</code> <span style={{ color: "var(--hs-text-muted)" }}>weight {w.weight} · alone {w.score}</span>
            <div style={{ marginTop: 2 }}>{w.why}</div>
            {w.excerpt && (
              <div style={{ fontFamily: "var(--hs-font-mono)", fontSize: 12, marginTop: 3, background: "var(--hs-code-bg)", padding: 4, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                <span style={{ color: "var(--hs-text-muted)" }}>{w.excerpt.before}</span><mark className="hs-mark">{w.excerpt.match}</mark><span style={{ color: "var(--hs-text-muted)" }}>{w.excerpt.after}</span>
              </div>)}
          </div>))}
      </div>

      <div style={styles.card} data-testid="review-model">
        <h3 style={{ marginTop: 0 }}>Claude’s verdict</h3>
        {data.model ? (
          <div style={{ fontSize: 12 }}>
            <strong style={{ color: LABEL_COLORS[data.model.label] }}>{data.model.label}</strong> · confidence {data.model.confidence ?? "—"} · <code>{data.model.model || "?"}</code>
            <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{data.model.reasoning || "no reasoning recorded"}</div>
          </div>) : <div style={{ color: "var(--hs-text-muted)", fontSize: 12 }}>The model has not looked at this item.</div>}
      </div>

      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Content {data.snapshot && !data.snapshot.content_missing &&
          <span style={{ fontSize: 12, color: "var(--hs-text-muted)", fontWeight: 400 }}> — {hl.length} highlighted match{hl.length === 1 ? "" : "es"} (red)</span>}</h3>
        {!data.snapshot && <div style={{ color: "var(--hs-text-muted)", fontSize: 12 }} data-testid="no-snapshot">Nothing has been fetched for this item yet (it was created by a user report).</div>}
        {data.snapshot?.content_missing && <div style={{ color: "var(--hs-danger)", fontSize: 12 }}>Content missing from storage — use Sources → Refetch missing content.</div>}
        {data.snapshot && !data.snapshot.content_missing && (
          <>
            <div style={{ marginBottom: 6 }}>
              <button data-testid="jump-hl" style={styles.btnDim} onClick={jump} disabled={hl.length === 0}>
                Jump to next highlight {hl.length > 0 && `(${cur + 1 > 0 ? cur + 1 : 0}/${hl.length})`}
              </button>
              <span style={{ fontSize: 12, color: "var(--hs-text-muted)", marginLeft: 8 }}>{data.snapshot.note}</span>
            </div>
            <HighlightedText text={data.snapshot.text} highlights={hl} current={cur} setRef={setRef} />
            {data.snapshot.signals.length > 0 && (
              <div style={{ fontSize: 12, marginTop: 6 }} data-testid="signals">
                Signals without a location in the text:
                {data.snapshot.signals.map((s, i) => <div key={i} style={{ fontFamily: "var(--hs-font-mono)" }}>{s.rule_id}: {s.evidence}</div>)}
              </div>)}
          </>)}
      </div>

      <div style={styles.card} data-testid="review-actions">
        <input data-testid="review-note" style={{ ...styles.input, width: 420, marginBottom: 8 }} placeholder="note (optional)"
               value={note} onChange={(e) => setNote(e.target.value)} />
        <div>
          <button data-testid="btn-benign" style={styles.btn} onClick={() => decide("benign")} disabled={!canLabel}>Benign (b)</button>{" "}
          <button data-testid="btn-unsure" style={styles.btnDim} onClick={() => decide("unsure")} disabled={!canLabel}>Unsure — skip, keep (u)</button>{" "}
          <button data-testid="btn-malicious" style={{ ...styles.btn, background: armed ? "var(--hs-danger)" : undefined }}
                  onClick={() => decide("malicious")} disabled={!canLabel}>Malicious (m, m)</button>{" "}
          <button data-testid="btn-next" style={styles.btnDim} onClick={next}>Next (n)</button>
        </div>
        {data.why_flagged.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 12 }} data-testid="rule-feedback">
            <select data-testid="feedback-rule" style={styles.select} value={feedbackRule} onChange={(e) => setFeedbackRule(e.target.value)}>
              <option value="">Rule too broad…</option>
              {data.why_flagged.map((w) => <option key={w.rule_id} value={w.rule_id}>{w.rule_id}</option>)}
            </select>{" "}
            <input data-testid="feedback-note" style={{ ...styles.input, width: 300 }} placeholder="why is it too broad?"
                   value={feedbackNote} onChange={(e) => setFeedbackNote(e.target.value)} />{" "}
            <button data-testid="feedback-send" style={styles.btnDim} onClick={sendFeedback} disabled={!feedbackRule}>Record</button>
          </div>)}
      </div>
    </div>
  );
}
